import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { env } from '@/app/config/env';
import type { HierarchyLevel, HierarchyNode } from '../models/hierarchyScope';
import { mockHierarchyOptions, mockTemplateCategories } from '../__mocks__/templateHierarchy.mock';
import type { TemplateCategoryOption } from '../models/template.types';

const FALLBACK = 'Unable to load the hierarchy options. Please try again.';

/* A refusal is the dropdown's news, not grounds to throw the user out of the shell. */
const allowForbidden = { _allowForbidden: true } as const;

/**
 * One level's Active values under any of the selected parents --
 * `GET /template-hierarchy/{level}?parentIds=1,2`.
 *
 * The server confines them to the caller's entities and labels each
 * `CODE + Name (Parent Code)`. Entities take no parents; any other level with
 * none selected has no options, so nothing is asked.
 */
export async function listHierarchyOptions(
  level: HierarchyLevel,
  parentIds: readonly number[],
): Promise<HierarchyNode[]> {
  if (level !== 'entities' && parentIds.length === 0) return [];
  if (env.ENABLE_MOCKS) return mockHierarchyOptions(level, parentIds);

  const params = level === 'entities' ? {} : { parentIds: parentIds.join(',') };
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<HierarchyNode[]>>(`/template-hierarchy/${level}`, { ...allowForbidden, params }),
    FALLBACK,
  );
}

/**
 * The Category master's active product categories for a PR Template -- `GET /template-hierarchy/categories?
 * legalEntityIds=1,2`: the entities' own and the shared ones, labelled `CODE - Name`. The builder's Category
 * dropdown and New Purchase Request's category filter read it.
 */
export async function listTemplateCategories(legalEntityIds: readonly number[]): Promise<TemplateCategoryOption[]> {
  if (env.ENABLE_MOCKS) return mockTemplateCategories(legalEntityIds);
  const params = legalEntityIds.length > 0 ? { legalEntityIds: legalEntityIds.join(',') } : {};
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<TemplateCategoryOption[]>>('/template-hierarchy/categories', {
      ...allowForbidden,
      params,
    }),
    'Unable to load the categories. Please try again.',
  );
}